import { ArrowUpLeft, CheckCircle, Hourglass03, LinkExternal01 } from "@untitledui/icons";
import { PARTNER_PANEL_CLOSED_DEFAULT_MESSAGE } from "@mfa-aff/shared";
import { MizbanfaLockup, ThemeToggle, cx, jalaliParts } from "@mfa-aff/ui";
import art from "@mfa-aff/ui/assets/illustrations/onboarding.webp";
import { BadgeWithDot } from "@mfa-aff/ui/components/base/badges/badges";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { BackgroundPattern } from "@mfa-aff/ui/components/shared-assets/background-patterns/index";

const SAFE = [
    "لینک‌ها و کدهای تخفیفتان همچنان کار می‌کنند و فروش‌ها به نام شما ثبت می‌شود.",
    "پورسانت‌ها و موجودی قبلی شما محفوظ است و دست نمی‌خورد.",
    "پس از باز شدن پنل، با همان حساب میزبان‌فا وارد می‌شوید.",
];

/**
 * Full-page «در حال آماده‌سازی» screen shown instead of the whole panel while the owner keeps it
 * closed (`features.partner_panel_open`). Same visual language as the sign-in page: logo header,
 * brand-gradient art panel with the grid pattern; one column on mobile (art first, compact).
 */
export const PanelClosedPage = ({ message }: { message?: string | null }) => (
    <div className="flex min-h-dvh flex-col bg-primary">
        <header className="flex items-center justify-between gap-4 px-4 pt-6 md:px-8 md:pt-8">
            <MizbanfaLockup caption="بازاریابی" size="md" />
            <ThemeToggle />
        </header>

        <main id="main" className="flex flex-1 items-center justify-center px-4 py-8 md:px-8 md:py-12">
            <div className="grid w-full max-w-6xl grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16">
                <div className="flex min-w-0 flex-col gap-6 md:gap-8">
                    <div className="flex flex-col items-start gap-4">
                        <BadgeWithDot type="pill-color" size="md" color="warning">
                            در حال آماده‌سازی
                        </BadgeWithDot>
                        <h1 className="text-display-xs font-semibold text-balance text-primary md:text-display-md">پنل بازاریابی به‌زودی باز می‌شود</h1>
                        <p className="text-md text-pretty text-tertiary md:text-lg">{message?.trim() || PARTNER_PANEL_CLOSED_DEFAULT_MESSAGE}</p>
                    </div>

                    <ul className="flex flex-col gap-3 rounded-2xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:p-5">
                        {SAFE.map((t) => (
                            <li key={t} className="flex gap-2.5 text-sm text-secondary md:text-md">
                                <CheckCircle className="mt-0.5 size-5 shrink-0 text-fg-success-secondary" aria-hidden="true" />
                                <span>{t}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        <Button size="lg" href="https://mizbanfa.net" iconTrailing={ArrowUpLeft}>
                            بازگشت به میزبان‌فا
                        </Button>
                        <Button size="lg" color="secondary" href="https://my.mizbanfa.net" target="_blank" rel="noopener noreferrer" iconTrailing={LinkExternal01}>
                            ناحیهٔ کاربری میزبان‌فا
                        </Button>
                    </div>
                </div>

                <div
                    className={cx(
                        "relative isolate order-first flex items-center justify-center overflow-hidden rounded-3xl p-6 md:p-10 lg:order-last lg:min-h-[32rem]",
                        "bg-linear-to-br from-brand-600 to-brand-900 dark:from-brand-800 dark:to-brand-950",
                    )}
                    aria-hidden="true"
                >
                    <div className="pointer-events-none absolute -end-40 -top-40 -z-10 text-white/10">
                        <BackgroundPattern pattern="grid" size="lg" />
                    </div>
                    <div className="pointer-events-none absolute -bottom-32 -start-32 -z-10 size-96 rounded-full bg-accent-500/20 blur-3xl" />
                    <div className="absolute start-4 top-4 flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white ring-1 ring-white/20 backdrop-blur-sm ring-inset md:start-6 md:top-6">
                        <Hourglass03 className="size-4" />
                        به‌زودی
                    </div>
                    <img src={art} alt="" className="max-h-44 w-full max-w-md object-contain drop-shadow-2xl sm:max-h-56 md:max-h-80 lg:max-h-[26rem]" />
                </div>
            </div>
        </main>

        <footer className="flex flex-col gap-1 px-4 pb-6 text-xs text-quaternary md:flex-row md:items-center md:justify-between md:px-8 md:pb-8">
            <p>© {jalaliParts(new Date()).year} میزبان‌فا</p>
            <p>پرسشی دارید؟ از ناحیهٔ کاربری میزبان‌فا تیکت پشتیبانی بفرستید.</p>
        </footer>
    </div>
);
